import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Mail, AlertCircle, Clock } from "lucide-react";

const Th = ({ children }) => (
  <th className="text-left px-2 py-1.5 label-mono text-white whitespace-nowrap">{children}</th>
);
const Field = ({ label, children }) => (
  <label className="block">
    <div className="label-mono mb-1">{label}</div>
    {children}
  </label>
);
const TabBtn = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-2 text-xs font-mono font-bold flex items-center gap-1 -mb-px border-b-2 ${
      active ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"
    }`}
  >
    {children}
  </button>
);

/**
 * Bulk-reminders inbox: overdue + follow-up tabs, per-row sender / template overrides,
 * inline auto-schedule + cooldown config strip. Extracted from Invoices.jsx.
 */
export default function RemindersModal({ senders, onClose, onSent, onAddSender }) {
  const [pending, setPending] = useState({ overdue: [], follow_up: [], counts: { overdue: 0, follow_up: 0, total: 0 } });
  const [config, setConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState("overdue"); // "overdue" | "follow_up"
  const [selected, setSelected] = useState({});
  const [rowOverrides, setRowOverrides] = useState({});
  const [defaultSenderId, setDefaultSenderId] = useState(senders[0]?.id || "");
  const [defaultTemplateId, setDefaultTemplateId] = useState("");
  const [sending, setSending] = useState(false);

  const refresh = async () => {
    try {
      const [p, c, t] = await Promise.all([
        api.get("/invoices/reminders/pending"),
        api.get("/invoices/reminders/config"),
        api.get("/invoice-email-templates").catch(() => ({ data: [] })),
      ]);
      setPending(p.data);
      setConfig(c.data);
      setTemplates(t.data || []);
      const tpl = (t.data || []).find((x) => x.status_trigger === "reminder");
      if (tpl && !defaultTemplateId) setDefaultTemplateId(tpl.id);
      if ((p.data.counts.overdue || 0) === 0 && (p.data.counts.follow_up || 0) > 0) setTab("follow_up");
    } catch { toast.error("Could not load pending reminders"); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const rows = tab === "overdue" ? pending.overdue : pending.follow_up;
  const allSelected = rows.length > 0 && rows.every((r) => selected[r.invoice_id]);
  const someSelected = rows.some((r) => selected[r.invoice_id]);

  const toggleAll = () => {
    const next = { ...selected };
    if (allSelected) rows.forEach((r) => { delete next[r.invoice_id]; });
    else rows.forEach((r) => { next[r.invoice_id] = true; });
    setSelected(next);
  };

  const setOverride = (id, key, value) => {
    setRowOverrides((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: value } }));
  };

  const updateConfig = async (patch) => {
    try {
      const { data } = await api.patch("/invoices/reminders/config", patch);
      setConfig(data);
      toast.success("Settings updated");
    } catch { toast.error("Could not update settings"); }
  };

  const sendSelected = async () => {
    const items = [];
    for (const bucket of ["overdue", "follow_up"]) {
      for (const r of pending[bucket] || []) {
        if (!selected[r.invoice_id]) continue;
        const ov = rowOverrides[r.invoice_id] || {};
        items.push({
          invoice_id: r.invoice_id,
          sender_id: ov.sender_id || defaultSenderId,
          template_id: ov.template_id || defaultTemplateId,
          to: ov.to || r.client_email || "",
          reminder_kind: bucket,
        });
      }
    }
    if (items.length === 0) { toast.error("Select at least one invoice"); return; }
    if (!defaultSenderId && items.some((i) => !i.sender_id)) { toast.error("Choose a default sender"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/invoices/reminders/send", {
        items,
        default_sender_id: defaultSenderId,
        default_template_id: defaultTemplateId,
      });
      const okN = data.sent.length;
      const failN = data.failed.length;
      if (okN > 0) toast.success(`Sent ${okN} reminder${okN > 1 ? "s" : ""}${failN ? ` · ${failN} failed` : ""}`);
      else toast.error(`All ${failN} failed — check sender + client emails`);
      onSent?.();
      await refresh();
      setSelected({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="brut-card max-w-5xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 pt-6 pb-3 border-b border-zinc-200 bg-white shrink-0">
          <div>
            <div className="label-mono">FOLLOW UP · GET PAID</div>
            <h2 className="font-heading text-2xl font-black">Invoice Reminders</h2>
            <p className="text-xs font-mono text-zinc-600 mt-1">
              Overdue and follow-up invoices needing a nudge. {config?.skip_within_days ?? 5}-day cooldown enforced.
            </p>
          </div>
          <button type="button" onClick={onClose} className="brut-btn-secondary p-1.5 ml-3"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          {config && (
            <div className="border border-zinc-900 bg-zinc-50 p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
              <label className="flex items-center gap-2" data-testid="auto-schedule-toggle">
                <input type="checkbox" checked={config.enabled} onChange={(e) => updateConfig({ enabled: e.target.checked })} />
                AUTO-SCHEDULE (Mon 9 AM IST)
              </label>
              <label className="flex items-center gap-1">
                FOLLOW-UP AFTER
                <input
                  type="number" min={1} max={90}
                  value={config.follow_up_days}
                  onChange={(e) => setConfig({ ...config, follow_up_days: Number(e.target.value) })}
                  onBlur={(e) => updateConfig({ follow_up_days: Number(e.target.value) })}
                  className="brut-input w-14 px-1 py-0.5 text-xs ml-1"
                  data-testid="follow-up-days-input"
                />
                DAYS
              </label>
              <label className="flex items-center gap-1">
                SKIP IF SENT IN LAST
                <input
                  type="number" min={0} max={30}
                  value={config.skip_within_days}
                  onChange={(e) => setConfig({ ...config, skip_within_days: Number(e.target.value) })}
                  onBlur={(e) => updateConfig({ skip_within_days: Number(e.target.value) })}
                  className="brut-input w-14 px-1 py-0.5 text-xs ml-1"
                  data-testid="skip-days-input"
                />
                DAYS
              </label>
              <div className="text-zinc-500">
                {config.last_run_at ? <>LAST AUTO RUN: <b>{config.last_run_at.slice(0, 16).replace("T", " ")}</b></> : "AUTO HAS NEVER RUN"}
              </div>
            </div>
          )}

          <StatementAutoConfig />

          <RevenueDigestConfig />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="DEFAULT SENDER">
              {senders.length === 0 ? (
                <button type="button" onClick={onAddSender} className="brut-input w-full px-3 py-2 text-sm text-left underline text-orange-700" data-testid="reminders-add-sender">
                  No sender configured — add one →
                </button>
              ) : (
                <select value={defaultSenderId} onChange={(e) => setDefaultSenderId(e.target.value)} className="brut-input w-full px-3 py-2 text-sm font-mono" data-testid="reminders-default-sender">
                  {senders.map((s) => <option key={s.id} value={s.id}>{s.display_name} &lt;{s.email}&gt;</option>)}
                </select>
              )}
            </Field>
            <Field label="DEFAULT TEMPLATE">
              <select value={defaultTemplateId} onChange={(e) => setDefaultTemplateId(e.target.value)} className="brut-input w-full px-3 py-2 text-sm font-mono" data-testid="reminders-default-template">
                <option value="">— inline default —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.status_trigger ? ` · ${t.status_trigger}` : ""}</option>)}
              </select>
            </Field>
          </div>

          <div className="flex gap-1 border-b border-zinc-900">
            <TabBtn active={tab === "overdue"} onClick={() => setTab("overdue")} testId="tab-overdue">
              <AlertCircle className="w-3.5 h-3.5" /> OVERDUE ({pending.counts.overdue})
            </TabBtn>
            <TabBtn active={tab === "follow_up"} onClick={() => setTab("follow_up")} testId="tab-follow-up">
              <Clock className="w-3.5 h-3.5" /> FOLLOW-UP ({pending.counts.follow_up})
            </TabBtn>
          </div>

          <div className="border border-zinc-900 overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-zinc-900 text-white">
                  <th className="px-2 py-1.5 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all-reminders" /></th>
                  <Th>Invoice</Th>
                  <Th>Client</Th>
                  <Th>Amount</Th>
                  <Th>{tab === "overdue" ? "Overdue By" : "Days Since Sent"}</Th>
                  <Th>Recipient</Th>
                  <Th>Sender Override</Th>
                  <Th>Template Override</Th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-8 text-zinc-400 font-mono text-xs">NO INVOICES IN THIS BUCKET — YOU&apos;RE ALL CAUGHT UP</td></tr>
                )}
                {rows.map((r) => {
                  const ov = rowOverrides[r.invoice_id] || {};
                  return (
                    <tr key={r.invoice_id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`reminder-row-${r.invoice_id}`}>
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={!!selected[r.invoice_id]}
                          onChange={(e) => setSelected({ ...selected, [r.invoice_id]: e.target.checked })}
                          data-testid={`select-reminder-${r.invoice_id}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 font-mono text-xs font-bold">{r.invoice_number}</td>
                      <td className="px-2 py-1.5 text-xs">{r.client_name}</td>
                      <td className="px-2 py-1.5 font-mono text-xs">{r.amount_display}</td>
                      <td className="px-2 py-1.5 font-mono text-xs">
                        {tab === "overdue"
                          ? <span className="text-red-700 font-bold">{r.days_overdue}d</span>
                          : <span>{r.days_since_last_email ?? "—"}d</span>}
                      </td>
                      <td className="px-2 py-1.5">
                        <input
                          type="email"
                          value={ov.to !== undefined ? ov.to : (r.client_email || "")}
                          onChange={(e) => setOverride(r.invoice_id, "to", e.target.value)}
                          placeholder="client email…"
                          className={`brut-input px-1 py-0.5 text-xs font-mono w-44 ${!r.client_email && ov.to === undefined ? "border-red-500" : ""}`}
                          data-testid={`reminder-to-${r.invoice_id}`}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <select
                          value={ov.sender_id || ""}
                          onChange={(e) => setOverride(r.invoice_id, "sender_id", e.target.value)}
                          className="brut-input px-1 py-0.5 text-xs font-mono max-w-[140px]"
                        >
                          <option value="">— default —</option>
                          {senders.map((s) => <option key={s.id} value={s.id}>{s.email}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <select
                          value={ov.template_id || ""}
                          onChange={(e) => setOverride(r.invoice_id, "template_id", e.target.value)}
                          className="brut-input px-1 py-0.5 text-xs font-mono max-w-[140px]"
                        >
                          <option value="">— default —</option>
                          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-between items-center gap-2 px-6 py-3 border-t border-zinc-300 bg-white shrink-0">
          <div className="text-xs font-mono text-zinc-600">
            {someSelected
              ? `${Object.values(selected).filter(Boolean).length} selected`
              : `${pending.counts.total} pending across both buckets`}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CLOSE</button>
            <button
              type="button"
              onClick={sendSelected}
              disabled={sending || !someSelected || senders.length === 0}
              className="brut-btn px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60"
              data-testid="send-reminders-btn"
            >
              <Mail className="w-4 h-4" /> {sending ? "SENDING…" : "SEND SELECTED REMINDERS"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatementAutoConfig() {
  const [cfg, setCfg] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get("/clients/statements/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const update = async (patch) => {
    try {
      const { data } = await api.patch("/clients/statements/config", patch);
      setCfg(data);
      toast.success("Statement schedule updated");
    } catch { toast.error("Update failed"); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/clients/statements/run-now");
      if (data.skipped) toast.error(`Skipped: ${data.skipped}`);
      else toast.success(`Statements: ${data.sent} sent · ${data.skipped} skipped · ${data.failed} failed (${data.month})`, { duration: 8000 });
      api.get("/clients/statements/config").then((r) => setCfg(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Run failed"); }
    setRunning(false);
  };

  if (!cfg) return null;
  return (
    <div className="border border-indigo-700 bg-indigo-50 p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono items-center" data-testid="statement-auto-config">
      <label className="flex items-center gap-2" data-testid="statement-auto-toggle">
        <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => update({ enabled: e.target.checked })} />
        AUTO MONTHLY STATEMENTS
      </label>
      <label className="flex items-center gap-1">
        ON DAY
        <input
          type="number" min={1} max={28}
          value={cfg.day_of_month}
          onChange={(e) => setCfg({ ...cfg, day_of_month: Number(e.target.value) })}
          onBlur={(e) => update({ day_of_month: Number(e.target.value) })}
          className="brut-input w-14 px-1 py-0.5 text-xs ml-1"
          data-testid="statement-day-input"
        />
        @ {cfg.hour_ist}:00 IST
      </label>
      <label className="flex items-center gap-2" data-testid="statement-only-outstanding-toggle">
        <input type="checkbox" checked={!!cfg.only_outstanding} onChange={(e) => update({ only_outstanding: e.target.checked })} />
        ONLY CLIENTS WITH BALANCE DUE
      </label>
      <div className="flex items-center justify-between gap-2">
        <span className="text-zinc-500">
          {cfg.last_run_at ? <>LAST: <b>{cfg.last_run_at.slice(0, 16).replace("T", " ")}</b></> : "NEVER RUN"}
        </span>
        <button type="button" onClick={runNow} disabled={running} className="brut-btn-secondary px-2 py-1 text-[10px] border-indigo-700 text-indigo-800 hover:bg-indigo-100 disabled:opacity-60" data-testid="statement-run-now-btn">
          {running ? "RUNNING…" : "RUN NOW"}
        </button>
      </div>
    </div>
  );
}

function RevenueDigestConfig() {
  const [cfg, setCfg] = useState(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get("/reports/revenue-digest/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const update = async (patch) => {
    try {
      const { data } = await api.patch("/reports/revenue-digest/config", patch);
      setCfg(data);
      toast.success("Digest schedule updated");
    } catch { toast.error("Update failed"); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/reports/revenue-digest/run-now");
      if (data.skipped) toast.error(`Skipped: ${data.skipped}`);
      else toast.success(`Digest for ${data.month} emailed to ${data.to} (${data.clients} clients)`, { duration: 8000 });
      api.get("/reports/revenue-digest/config").then((r) => setCfg(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Run failed"); }
    setRunning(false);
  };

  if (!cfg) return null;
  return (
    <div className="border border-emerald-700 bg-emerald-50 p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono items-center" data-testid="revenue-digest-config">
      <label className="flex items-center gap-2" data-testid="digest-toggle">
        <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => update({ enabled: e.target.checked })} />
        MONTHLY REVENUE DIGEST
      </label>
      <label className="flex items-center gap-1">
        ON DAY
        <input
          type="number" min={1} max={28}
          value={cfg.day_of_month}
          onChange={(e) => setCfg({ ...cfg, day_of_month: Number(e.target.value) })}
          onBlur={(e) => update({ day_of_month: Number(e.target.value) })}
          className="brut-input w-14 px-1 py-0.5 text-xs ml-1"
          data-testid="digest-day-input"
        />
        @ {cfg.hour_ist}:00 IST
      </label>
      <input
        type="email" placeholder="Recipient (default: admin email)"
        value={cfg.recipient || ""}
        onChange={(e) => setCfg({ ...cfg, recipient: e.target.value })}
        onBlur={(e) => update({ recipient: e.target.value.trim() || null })}
        className="brut-input px-2 py-1 text-xs"
        data-testid="digest-recipient-input"
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-zinc-500">
          {cfg.last_run_at ? <>LAST: <b>{cfg.last_run_at.slice(0, 16).replace("T", " ")}</b></> : "NEVER RUN"}
        </span>
        <button type="button" onClick={runNow} disabled={running} className="brut-btn-secondary px-2 py-1 text-[10px] border-emerald-700 text-emerald-800 hover:bg-emerald-100 disabled:opacity-60" data-testid="digest-run-now-btn">
          {running ? "RUNNING…" : "RUN NOW"}
        </button>
      </div>
    </div>
  );
}
